import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Upload,
  X,
  Tag,
  Edit2,
  Trash2,
  Calendar,
  User,
  Image as ImageIcon,
  FileText
} from 'lucide-react';
import { compressImage } from '../utils/imageCompression';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';

function AlbumDetail({ user }) {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editPhotoDialogOpen, setEditPhotoDialogOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [viewPhotoDialogOpen, setViewPhotoDialogOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [photoFormData, setPhotoFormData] = useState({
    caption: '',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  useEffect(() => {
    fetchAlbumAndPhotos();
  }, [albumId]);

  const fetchAlbumAndPhotos = async () => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      // Fetch album details
      const albumResponse = await fetch(`${backendUrl}/api/photo-albums/${albumId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!albumResponse.ok) {
        if (albumResponse.status === 403) {
          toast.error('You do not have access to this album');
          navigate('/photo-albums');
          return;
        }
        throw new Error('Failed to fetch album');
      }

      const albumData = await albumResponse.json();
      setAlbum(albumData);

      // Fetch photos
      const photosResponse = await fetch(`${backendUrl}/api/photo-albums/${albumId}/photos`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (photosResponse.ok) {
        const photosData = await photosResponse.json();
        setPhotos(photosData);
      }
    } catch (error) {
      console.error('Error fetching album:', error);
      toast.error('Failed to load album');
    } finally {
      setLoading(false);
    }
  };

  const isPdfFile = (file) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  const isPdfPhoto = (photo) => ((photo?.file_path || photo?.photo_path || '')).toLowerCase().includes('.pdf');

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    console.log('[AlbumDetail] Files selected:', files.length, files.map(f => f.name));
    if (files.length > 0) {
      setUploadFiles(files);
      if (isPdfFile(files[0])) {
        setUploadPreview(null);
        return;
      }
      // Create preview for first file
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadPreview(reader.result);
        console.log('[AlbumDetail] Preview created for:', files[0].name);
      };
      reader.readAsDataURL(files[0]);
    }
  };

  const handleUploadPhoto = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('[AlbumDetail] Upload button clicked, files:', uploadFiles.length);
    
    if (!uploadFiles || uploadFiles.length === 0) {
      toast.error('Please select at least one photo to upload');
      return;
    }

    // Validate all files
    for (let file of uploadFiles) {
      if (!file.type.startsWith('image/') && !isPdfFile(file)) {
        toast.error(`${file.name} is not a valid image or PDF file`);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 10MB`);
        return;
      }
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: uploadFiles.length });
    
    let successCount = 0;
    let failCount = 0;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      console.log('[AlbumDetail] Starting upload to:', `${backendUrl}/api/photo-albums/${albumId}/photos`);
      
      // Upload each file sequentially
      for (let i = 0; i < uploadFiles.length; i++) {
        const file = uploadFiles[i];
        setUploadProgress({ current: i + 1, total: uploadFiles.length });
        
        try {
          toast.info(`Processing ${file.name}... (${i + 1}/${uploadFiles.length})`);
          
          console.log(`[AlbumDetail] Compressing ${file.name}, original size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
          
          // Compress image before upload (PDFs are uploaded as-is)
          let compressedFile = file;
          if (!isPdfFile(file)) {
            compressedFile = await compressImage(file, 1280, 1280, 0.8);
            if (!compressedFile || compressedFile.size === 0) {
              throw new Error('Image compression failed');
            }
          }

          console.log(`[AlbumDetail] Uploading ${file.name}, compressed size: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);

          // Upload using axios with timeout (Authorization header added automatically by axios interceptor)
          const formData = new FormData();
          formData.append('file', compressedFile);
          formData.append('caption', photoFormData.caption);
          formData.append('tags', JSON.stringify(photoFormData.tags));

          await axios.post(`${backendUrl}/api/photo-albums/${albumId}/photos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 30000 // 30 second timeout
          });
          
          console.log(`[AlbumDetail] Successfully uploaded ${file.name}`);
          successCount++;
        } catch (error) {
          console.error(`[AlbumDetail] Failed to upload ${file.name}:`, error);
          failCount++;
          toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || error.message}`);
        }
      }
      
      // Show summary
      if (successCount > 0) {
        toast.success(`Successfully uploaded ${successCount} photo${successCount > 1 ? 's' : ''}!`);
      }
      if (failCount > 0) {
        toast.error(`Failed to upload ${failCount} photo${failCount > 1 ? 's' : ''}`);
      }
      
      // Close dialog and refresh if at least one succeeded
      if (successCount > 0) {
        setUploadDialogOpen(false);
        setUploadFiles([]);
        setUploadPreview(null);
        setPhotoFormData({ caption: '', tags: [] });
        await fetchAlbumAndPhotos();
      }
    } catch (error) {
      console.error('[AlbumDetail] Upload error:', error);
      toast.error('Upload failed: ' + (error.message || 'Unknown error'));
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const handleUpdatePhoto = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(
        `${backendUrl}/api/photo-albums/${albumId}/photos/${selectedPhoto.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(photoFormData)
        }
      );

      if (!response.ok) throw new Error('Failed to update photo');

      toast.success('Photo updated successfully!');
      setEditPhotoDialogOpen(false);
      setSelectedPhoto(null);
      setPhotoFormData({ caption: '', tags: [] });
      fetchAlbumAndPhotos();
    } catch (error) {
      console.error('Error updating photo:', error);
      toast.error('Failed to update photo');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(
        `${backendUrl}/api/photo-albums/${albumId}/photos/${photoId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to delete photo');

      toast.success('Photo deleted successfully!');
      fetchAlbumAndPhotos();
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Failed to delete photo');
    }
  };

  const openEditPhotoDialog = (photo) => {
    setSelectedPhoto(photo);
    setPhotoFormData({
      caption: photo.caption || '',
      tags: photo.tags || []
    });
    setEditPhotoDialogOpen(true);
  };

  const openViewPhotoDialog = (photo) => {
    setSelectedPhoto(photo);
    setViewPhotoDialogOpen(true);
  };
  
  const openLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };
  
  const goToPrevious = () => {
    setCurrentPhotoIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };
  
  const goToNext = () => {
    setCurrentPhotoIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };
  
  const handleKeyDown = (e) => {
    if (!lightboxOpen) return;
    if (e.key === 'ArrowLeft') goToPrevious();
    if (e.key === 'ArrowRight') goToNext();
    if (e.key === 'Escape') setLightboxOpen(false);
  };
  
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, currentPhotoIndex]);
  
  // Swipe handlers for mobile
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      goToNext();
    }
    if (isRightSwipe) {
      goToPrevious();
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !photoFormData.tags.includes(tagInput.trim())) {
      setPhotoFormData({
        ...photoFormData,
        tags: [...photoFormData.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setPhotoFormData({
      ...photoFormData,
      tags: photoFormData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const getPhotoUrl = async (photo) => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/photo-albums/${albumId}/photos/${photo.id}/file`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        console.error('Failed to fetch photo:', response.status);
        return null;
      }
      
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error fetching photo:', error);
      return null;
    }
  };

  const [photoUrls, setPhotoUrls] = useState({});

  useEffect(() => {
    // Fetch photo URLs when photos change (in parallel for speed!)
    const fetchPhotoUrls = async () => {
      if (photos.length === 0) return;
      
      // Load all photos in parallel - MUCH faster!
      const photoPromises = photos.map(photo =>
        getPhotoUrl(photo)
          .then(url => ({ photoId: photo.id, url }))
          .catch(() => ({ photoId: photo.id, url: null }))
      );

      const results = await Promise.all(photoPromises);
      
      const urls = {};
      results.forEach(({ photoId, url }) => {
        if (url) urls[photoId] = url;
      });
      setPhotoUrls(urls);
    };

    if (photos.length > 0) {
      fetchPhotoUrls();
    }
  }, [photos, albumId]);

  const getPhotoDisplayUrl = (photo) => {
    return photoUrls[photo.id] || null;
  };

  const isOwner = album && user && album.user_id === user.id;
  const isAdmin = user && (user.role === 'admin' || user.role === 'supervisor');

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <p className="text-center text-gray-600">Album not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 pt-24 sm:pt-0">
        <Button
          variant="outline"
          size="lg"
          className="mb-6 flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 shadow-md hover:shadow-lg transition-all"
          onClick={() => navigate('/photo-albums')}
        >
          <ArrowLeft className="h-5 w-5" />
          Back to Photo Albums
        </Button>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{album.name}</h1>
            {album.description && (
              <p className="text-gray-600 mb-2">{album.description}</p>
            )}
            <p className="text-sm text-gray-500">
              Created by {album.user_name} • {photos.length} photo{photos.length !== 1 ? 's' : ''}
            </p>
          </div>
          {(isOwner || isAdmin) && (
            <div className="ml-6 flex gap-3">
              {photos.length === 0 && (
                <div className="text-right">
                  <p className="text-sm text-gray-500 mb-2">Start adding photos to your gallery</p>
                </div>
              )}
              <Dialog 
                open={uploadDialogOpen} 
                onOpenChange={(open) => {
                  // Prevent closing during upload
                  if (!uploading) {
                    setUploadDialogOpen(open);
                    // Reset form when closing
                    if (!open) {
                      setUploadFiles([]);
                      setUploadPreview(null);
                      setPhotoFormData({ caption: '', tags: [] });
                    }
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button size="lg" className="flex items-center gap-3 bg-green-600 hover:bg-green-700 px-6 py-3">
                    <Upload className="h-5 w-5" />
                    Upload Photos
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Upload Photos</DialogTitle>
                    <DialogDescription>
                      Add photos to your album (select multiple)
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleUploadPhoto} className="space-y-4">
                    <div>
                      <Label>Select Photos (multiple) *</Label>
                      <Input
                        type="file"
                        accept="image/*,application/pdf"
                        multiple
                        onChange={handleFileSelect}
                        required
                      />
                      {uploadFiles.length > 0 && (
                        <p className="text-sm text-gray-600 mt-2">
                          {uploadFiles.length} file{uploadFiles.length > 1 ? 's' : ''} selected
                        </p>
                      )}
                      {uploadFiles.length > 0 && isPdfFile(uploadFiles[0]) && (
                        <div className="mt-4 flex items-center justify-center gap-2 bg-red-50 border border-red-200 rounded-lg p-4">
                          <FileText className="w-8 h-8 text-red-500" />
                          <span className="text-sm font-semibold text-gray-800">{uploadFiles[0].name}</span>
                        </div>
                      )}
                      {uploadPreview && (
                        <div className="mt-4">
                          <img
                            src={uploadPreview}
                            alt="Preview"
                            className="max-h-64 w-auto mx-auto rounded-lg"
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="caption">Caption</Label>
                      <Textarea
                        id="caption"
                        value={photoFormData.caption}
                        onChange={(e) => setPhotoFormData({ ...photoFormData, caption: e.target.value })}
                        placeholder="Add a caption..."
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Tags</Label>
                      <div className="flex gap-2 mb-2">
                        <Input
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          placeholder="e.g., Framing, Electrical"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addTag();
                            }
                          }}
                        />
                        <Button type="button" onClick={addTag}>Add</Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {photoFormData.tags.map((tag) => (
                          <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                            {tag}
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() => removeTag(tag)}
                            />
                          </Badge>
                        ))}
                      </div>
                    </div>
                    {uploading && uploadProgress.total > 0 && (
                      <div className="p-3 bg-blue-900/30 border border-blue-700 rounded-lg">
                        <p className="text-sm text-blue-200">
                          Uploading {uploadProgress.current} of {uploadProgress.total}...
                        </p>
                        <div className="w-full bg-slate-700 rounded-full h-2 mt-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button 
                        type="submit" 
                        className="flex-1 bg-green-600 hover:bg-green-700" 
                        disabled={uploading}
                      >
                        {uploading ? (
                          <>
                            <Upload className="w-4 h-4 mr-2 animate-pulse" />
                            Uploading {uploadFiles.length > 1 ? `${uploadFiles.length} Photos` : 'Photo'}...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload {uploadFiles.length > 1 ? `${uploadFiles.length} Photos` : 'Photo'}
                          </>
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setUploadDialogOpen(false);
                          setUploadFiles([]);
                          setUploadPreview(null);
                          setPhotoFormData({ caption: '', tags: [] });
                        }}
                        disabled={uploading}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>
      </div>

      {photos.length === 0 ? (
        <div className="text-center py-16 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl border-2 border-dashed border-green-200">
          <div className="max-w-md mx-auto">
            <ImageIcon className="h-20 w-20 text-green-400 mx-auto mb-6" />
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Ready to Upload Photos?</h3>
            <p className="text-gray-600 mb-6">Start documenting your construction progress by uploading your first photo to this gallery.</p>
            {isOwner && (
              <Button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setUploadDialogOpen(true);
                }}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-lg"
              >
                <Upload className="h-6 w-6 mr-3" />
                Upload Your First Photo
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {photos.map((photo, index) => (
            <div
              key={photo.id}
              className="relative aspect-square bg-gray-200 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity group"
              onClick={() => {
                if (isPdfPhoto(photo)) {
                  const url = getPhotoDisplayUrl(photo);
                  if (url) window.open(url, '_blank');
                  return;
                }
                openLightbox(index);
              }}
            >
              {isPdfPhoto(photo) ? (
                <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 gap-2" data-testid={`pdf-tile-${photo.id}`}>
                  <FileText className="w-12 h-12 text-red-500" />
                  <span className="text-xs font-bold text-red-600 uppercase">PDF</span>
                  {photo.caption && <span className="text-[11px] text-gray-600 px-2 text-center line-clamp-2">{photo.caption}</span>}
                </div>
              ) : (
              <img
                src={getPhotoDisplayUrl(photo)}
                alt={photo.caption || 'Album photo'}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error('Failed to load photo:', photo.id);
                }}
              />
              )}
              {/* Overlay with info on hover */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-end p-2">
                <div className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity truncate w-full">
                  {photo.caption || 'View photo'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Lightbox Modal for Full-Screen Viewing with Swipe */}
      {lightboxOpen && photos[currentPhotoIndex] && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
            onClick={() => setLightboxOpen(false)}
          >
            <X className="w-8 h-8" />
          </button>
          
          {/* Photo Counter */}
          <div className="absolute top-4 left-4 text-white text-sm z-50">
            {currentPhotoIndex + 1} / {photos.length}
          </div>
          
          {/* Previous Button */}
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-3 z-50"
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          
          {/* Next Button */}
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-3 z-50"
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
          >
            <ArrowLeft className="w-6 h-6 rotate-180" />
          </button>
          
          {/* Main Image */}
          <div 
            className="max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            {isPdfPhoto(photos[currentPhotoIndex]) ? (
              <div className="flex flex-col items-center gap-4 bg-white rounded-xl p-10">
                <FileText className="w-16 h-16 text-red-500" />
                <button
                  onClick={() => { const u = getPhotoDisplayUrl(photos[currentPhotoIndex]); if (u) window.open(u, '_blank'); }}
                  className="px-5 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600"
                >
                  Open PDF
                </button>
              </div>
            ) : (
            <img
              src={getPhotoDisplayUrl(photos[currentPhotoIndex])}
              alt={photos[currentPhotoIndex].caption || 'Photo'}
              className="max-w-full max-h-full object-contain"
            />
            )}
          </div>
          
          {/* Photo Info */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6 text-white">
            <div className="max-w-4xl mx-auto">
              {photos[currentPhotoIndex].caption && (
                <p className="text-lg mb-2">{photos[currentPhotoIndex].caption}</p>
              )}
              <div className="flex items-center gap-4 text-sm text-gray-300">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {photos[currentPhotoIndex].uploader_name}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(photos[currentPhotoIndex].created_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}
                </span>
              </div>
              {photos[currentPhotoIndex].tags && photos[currentPhotoIndex].tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {photos[currentPhotoIndex].tags.map((tag) => (
                    <span key={tag} className="px-2 py-1 bg-white bg-opacity-20 rounded text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              {user && (photos[currentPhotoIndex].uploader_id === user.id || isAdmin) && (
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-white bg-opacity-20 border-white text-white hover:bg-opacity-30"
                    onClick={() => {
                      setLightboxOpen(false);
                      openEditPhotoDialog(photos[currentPhotoIndex]);
                    }}
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      setLightboxOpen(false);
                      handleDeletePhoto(photos[currentPhotoIndex].id);
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                  {isAdmin && photos[currentPhotoIndex].uploader_id !== user.id && (
                    <span className="text-xs text-yellow-300 self-center ml-2">
                      (Admin Override)
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Dialog open={editPhotoDialogOpen} onOpenChange={setEditPhotoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Photo</DialogTitle>
            <DialogDescription>
              Update photo caption and tags
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdatePhoto} className="space-y-4">
            <div>
              <Label htmlFor="edit-caption">Caption</Label>
              <Textarea
                id="edit-caption"
                value={photoFormData.caption}
                onChange={(e) => setPhotoFormData({ ...photoFormData, caption: e.target.value })}
                rows={3}
              />
            </div>
            <div>
              <Label>Tags</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="e.g., Framing, Electrical"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                />
                <Button type="button" onClick={addTag}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {photoFormData.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                    {tag}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => removeTag(tag)}
                    />
                  </Badge>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1">Update Photo</Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditPhotoDialogOpen(false);
                  setSelectedPhoto(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={viewPhotoDialogOpen} onOpenChange={setViewPhotoDialogOpen}>
        <DialogContent className="max-w-4xl">
          {selectedPhoto && (
            <div>
              {isPdfPhoto(selectedPhoto) ? (
                <div className="flex flex-col items-center gap-4 bg-red-50 border border-red-200 rounded-xl p-10 mb-4">
                  <FileText className="w-16 h-16 text-red-500" />
                  <button
                    onClick={() => { const u = getPhotoDisplayUrl(selectedPhoto); if (u) window.open(u, '_blank'); }}
                    className="px-5 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600"
                  >
                    Open PDF
                  </button>
                </div>
              ) : (
              <img
                src={getPhotoDisplayUrl(selectedPhoto)}
                alt={selectedPhoto.caption || 'Album photo'}
                className="w-full max-h-[70vh] object-contain mb-4"
              />
              )}
              {selectedPhoto.caption && (
                <p className="text-gray-700 mb-2">{selectedPhoto.caption}</p>
              )}
              {selectedPhoto.tags && selectedPhoto.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedPhoto.tags.map((tag) => (
                    <Badge key={tag} variant="secondary">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
              <div className="flex items-center text-sm text-gray-600 gap-4">
                <div className="flex items-center">
                  <User className="h-4 w-4 mr-1" />
                  {selectedPhoto.uploader_name}
                </div>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  {new Date(selectedPhoto.created_at).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {isOwner && photos.length > 0 && (
        <div className="fixed bottom-24 sm:bottom-20 right-4 sm:right-6 z-50">
          <Button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setUploadDialogOpen(true);
            }}
            className="h-14 w-14 rounded-full bg-green-600 hover:bg-green-700 shadow-lg hover:shadow-xl transition-all duration-200"
            title="Upload More Photos"
          >
            <Upload className="h-6 w-6" />
          </Button>
        </div>
      )}

      {/* Sticky Back Button for easy navigation */}
      <div className="fixed bottom-24 sm:bottom-20 left-4 sm:left-6 z-50">
        <Button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigate('/photo-albums');
          }}
          className="h-14 w-14 rounded-full bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
          title="Back to Main Photo Gallery"
        >
          <ArrowLeft className="h-6 w-6" />
        </Button>
      </div>
    </div>
  );
}

export default AlbumDetail;
